import TravelRequirementsNotice from "../components/travel/TravelRequirementsNotice";
import React, { useCallback, useEffect, useState } from "react";
import { useFlightSearchStore } from "../store/useFlightSearchStore";
import { FlightSearchService } from "../services/FlightSearchService";
import { RoundTripFlightMatch } from "../models/RoundTripFlightMatch";
import HomeButton from "../components/buttons/homeButton";
import { SelectorType } from "../models/FlightSearch";
import { ConnectionMode, FlightSearchData, TripType } from "../models/flightSearchData";
import FlightSearchSelection from "../components/selector/FlightSearchSelection";
import GroupTripButton from "../components/buttons/groupTripButton";
import DestinationGroup from "../components/list/destinationGroup";
import { FlightSearchBar } from "../components/search/flightSearchBar";
import { TravelDurationMap } from "../models/TravelDuration";
import {useTranslation} from "../i18n/LanguageProvider";
import {SearchRequest} from "../models/SearchRequest";

import MobileResults from "../mobile/MobileResults";
import useIsMobile from "../hooks/useIsMobile";

const ComparisonPage: React.FC = () => {
    const isMobile = useIsMobile();
  const searchData = useFlightSearchStore((state) => state.data);
  const [activeSelector, setActiveSelector] = useState<SelectorType | null>(null);
  const [flightSearchData, setFlightSearchData] = useState<FlightSearchData>(searchData);
  const [flightDeals, setFlightDeals] = useState<RoundTripFlightMatch[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {t} = useTranslation();

  const handleSearch = useCallback(async () => {
    if (
      !flightSearchData.dateFrom ||
      !flightSearchData.dateTo ||
      flightSearchData.airports.length === 0
    ) {
      console.warn(t("warning.missingSearchData"));
      return;
    }

    setIsLoading(true);
    const durationMeta = TravelDurationMap[flightSearchData.travelDuration];
    const durationFrom =
      flightSearchData.travelDuration === "custom"
        ? flightSearchData.minDurationDays ?? durationMeta.minDays
        : durationMeta.minDays;
    const durationTo =
      flightSearchData.travelDuration === "custom"
        ? flightSearchData.maxDurationDays ?? undefined
        : durationMeta.maxDays ?? undefined;
    const isOneWay = flightSearchData.tripType === TripType.OneWay;

    try {
      const payload: SearchRequest = {
        startAirport: flightSearchData.airports[0].code,
        startAirports: flightSearchData.airports.map((airport) => airport.code),
        dateFrom: flightSearchData.dateFrom.toISOString().split("T")[0],
        dateTo: flightSearchData.dateTo.toISOString().split("T")[0],
        tripType: flightSearchData.tripType,
        maxStops: flightSearchData.connectionMode === ConnectionMode.DirectAndOneStop ? 1 : 0,
        categories: flightSearchData.categories,
        durationFrom: isOneWay ? undefined : durationFrom,
        durationTo: isOneWay ? undefined : durationTo,
        destinationAirports: flightSearchData.destinationAirports.map((airport) => airport.code),
        destinationCountries: flightSearchData.destinationCountries.map((country) => country.code),
        travelProfile: flightSearchData.travelProfile,
      };

      const flight_rtf_results = await FlightSearchService.compareFlights(payload);
      setFlightDeals(flight_rtf_results);
    } catch (e) {
      console.error("Search failed", e);
    } finally {
      setIsLoading(false);
    }
  }, [flightSearchData, t]);

  useEffect(() => {
    handleSearch();
    // Run only once on page entry; later searches are triggered by the search button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedByDestination = flightDeals.reduce(
    (acc, flight) => {
      const city = flight.round_trip_flight.outbound_flight.destination_airport;
      if (!acc[city]) acc[city] = [];
      acc[city].push(flight);
      return acc;
    },
    {} as Record<string, RoundTripFlightMatch[]>
  );

  if (isMobile) {
    return <MobileResults flights={flightDeals} isLoading={isLoading} searchData={flightSearchData}
      setSearchData={setFlightSearchData} onSearch={handleSearch} />;
  }

  return (
    <div className="flex flex-col items-center justify-center gap-8 px-4 sm:pl-6 md:px-4 lg:pl-10 lg:pr-0 xl:px-28 py-5 sm:py-8 lg:py-10 transition-smooth">
      {/* Header */}
      <div className="flex flex-col md:flex-row lg:items-start xl:justify-between w-full gap-6 lg:gap-20">
        <div className="flex shrink-0 justify-start mt-1">
          <HomeButton />
        </div>

        <div className="w-full min-w-0 max-w-4xl flex flex-col gap-4">
          <FlightSearchBar
            activeSelector={activeSelector}
            setActiveSelector={setActiveSelector}
            searchData={flightSearchData}
            setSearchData={setFlightSearchData}
            onSearch={() => handleSearch()}
          />

          {activeSelector && (
            <FlightSearchSelection
              activeSelector={activeSelector}
              setActiveSelector={setActiveSelector}
              flightSearchData={flightSearchData}
              setFlightSearchData={setFlightSearchData}
            />
          )}
        </div>

        <div className="hidden xl:block justify-end mt-1.5">
          <GroupTripButton />
        </div>
      </div>

      <TravelRequirementsNotice active={!!flightSearchData.travelProfile} empty={!isLoading && flightDeals.length === 0} />
      {/* Title */}
      <div className="w-full text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#2D2D2D] transition-all duration-300">
          {t("comparison.title")}
        </h2>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-10 justify-start w-full px-0 sm:px-4 md:pl-10 lg:pl-10 md:pr-10 max-w-[1168px] transition-all duration-300">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#712FFF] border-t-transparent" />
          </div>
        ) : (
          <>
            {Object.entries(groupedByDestination).map(([city, flights]) => (
              <DestinationGroup key={city} city={city} flightMatches={flights} />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default ComparisonPage;
